import { z } from 'zod';
import { ActivationId } from './activation.ts';
import { Id, IsoDateTime } from './common.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.3; ADR-021): a **boundary hold** of a process instance (and its case) for a
 * business reason. Kept apart from `process.ts` so `actions.ts` (`RecoveryDecision`) can reference it without an
 * import cycle.
 *
 * Semantics (v1, no clock pause): in a held scope no new activation is spawned and no new engine wait is started.
 * In-flight tasks and service calls complete; their results, approval decisions, work-item completions and inbox
 * events are accepted and **parked** (`ParkedResult`); the handler sees them after `resume`. No deadline is paused:
 * a breach during a hold is recorded `breached_while_held`; a deadline due before `resumeAt` gets a
 * `deadline_at_risk` timeline warning. `scope: 'activation'` holds one activation; others continue.
 */
export const HoldScope = z.enum(['instance', 'activation']).meta({ id: 'HoldScope' });
export type HoldScope = z.infer<typeof HoldScope>;

/** Input of `ProcessOrchestrator.hold` and of `POST /cases/{caseId}/hold`. */
export const HoldRequest = z
  .object({
    reason: z.string().min(1).max(2000),
    /** Role that owns the hold and may resume it. */
    ownerRole: z.string().min(1),
    /** The activation the process resumes at (re-entry target after the hold). */
    resumeActivationId: ActivationId,
    /** Planned resume time (informational; nothing resumes automatically in v1). */
    resumeAt: IsoDateTime.optional(),
    scope: HoldScope.default('instance'),
    /** `scope: 'activation'`: the held activation (default `resumeActivationId`). */
    activationId: ActivationId.optional(),
    /** Evidence the resumer must provide (document types or free-text requirements). */
    evidenceRequired: z.array(z.string().min(1).max(200)).optional(),
  })
  .meta({ id: 'HoldRequest' });
export type HoldRequest = z.infer<typeof HoldRequest>;
export type HoldRequestInput = z.input<typeof HoldRequest>;

/** An open (or closed) hold as stored on the instance and projected on the case. */
export const Hold = HoldRequest.extend({
  holdId: Id,
  openedBy: z.string().min(1),
  openedAt: IsoDateTime,
  resumedBy: z.string().min(1).optional(),
  resumedAt: IsoDateTime.optional(),
}).meta({ id: 'Hold' });
export type Hold = z.infer<typeof Hold>;

/** Input of `ProcessOrchestrator.resume` and of `POST /cases/{caseId}/resume`. */
export const ResumeRequest = z
  .object({
    /** Evidence references (document ids, artifact ids, notes) satisfying `evidenceRequired`. */
    evidence: z.array(z.string().min(1).max(500)).default([]),
    note: z.string().max(2000).optional(),
  })
  .meta({ id: 'ResumeRequest' });
export type ResumeRequest = z.infer<typeof ResumeRequest>;

/** A result that arrived while its scope was held; delivered to the handler after resume, never dropped. */
export const ParkedResult = z
  .object({
    activationId: ActivationId,
    kind: z.enum(['task', 'service', 'work_item', 'approval', 'inbox']),
    receivedAt: IsoDateTime,
    /** Signal name the result is delivered on after resume. */
    signal: z.string().min(1).optional(),
  })
  .meta({ id: 'ParkedResult' });
export type ParkedResult = z.infer<typeof ParkedResult>;

// -----------------------------------------------------------------------------------------------
// Added by 21a (hold-01..hold-11): the run-side signals of a boundary hold
// -----------------------------------------------------------------------------------------------

/**
 * Added by 21a: the one engine signal that tells a run a hold opened (payload `HoldSignal`, dedupe key = hold id).
 * Every hold-aware wait of the SDK (`packages/sdk/src/flow-hold.ts`) also listens on it; the run then waits for
 * `holdResumeSignalName(holdId)` and the results that arrive meanwhile stay buffered in the engine (parked).
 */
export const HOLD_SIGNAL = 'process:hold';

/** Added by 21a: signal that closes hold `holdId` in the run (payload `HoldResumeSignal`, dedupe key = hold id). */
export function holdResumeSignalName(holdId: string): string {
  return `hold:resume:${holdId}`;
}

/** Added by 21a: payload of `HOLD_SIGNAL`. */
export const HoldSignal = z
  .object({ holdId: Id, scope: HoldScope, activationId: ActivationId.optional() })
  .meta({ id: 'HoldSignal' });
export type HoldSignal = z.infer<typeof HoldSignal>;

/**
 * Added by 21a: payload of `holdResumeSignalName(holdId)`. `rerouted`: the resume target had no activation yet and
 * the open activations (`abandoned`) were marked `skipped: superseded_route`; a hold-aware wait covered by the hold
 * throws `conflict` with `details.reason = HOLD_REROUTED_REASON` so the handler continues at `resumeActivationId`.
 */
export const HoldResumeSignal = z
  .object({
    holdId: Id,
    rerouted: z.boolean(),
    resumeActivationId: ActivationId,
    abandoned: z.array(ActivationId).default([]),
  })
  .meta({ id: 'HoldResumeSignal' });
export type HoldResumeSignal = z.infer<typeof HoldResumeSignal>;

/** Added by 21a: `PlatformError.details.reason` of the error a re-routed hold raises in the waiting handler. */
export const HOLD_REROUTED_REASON = 'hold_rerouted';

/** Added by 21a: the activation an `activation`-scoped hold holds (`activationId`, default `resumeActivationId`). */
export function heldActivationOf(hold: Pick<Hold, 'scope' | 'activationId' | 'resumeActivationId'>): string {
  return hold.activationId ?? hold.resumeActivationId;
}

/**
 * Added by 21a: true when `hold` covers `activationId` — every activation for `scope: 'instance'`, only the held
 * activation for `scope: 'activation'`. A wait or spawn without an activation is covered by instance holds only.
 */
export function holdCovers(
  hold: Pick<Hold, 'scope' | 'activationId' | 'resumeActivationId'>,
  activationId?: string,
): boolean {
  if (hold.scope === 'instance') return true;
  return activationId !== undefined && heldActivationOf(hold) === activationId;
}
